import React, { useEffect, useContext, useState } from "react";
import { isMobile } from "react-device-detect";
import { useQuery } from "@apollo/client";
import { useParams, useHistory, Link } from "react-router-dom";

import ReaderBlock from "./reader_block";
import { DataContext } from "../../../context/data-context";
import { LangItem } from "../../../models/lang";
import { GET_AUTHOR_READERS } from "./queries";
import BookInfo from "./bookInfo";
import { Helmet } from "react-helmet";
import LanguageSelect from "../../common/langSelection";
import { AuthorReaderItem } from "../../../models/authors";
import { hasPrivilege } from "../../../middleware/privileges";
import { SkeletonBooks } from "../../common/creator_ui";
import { getAuthCookie } from "../../../middleware/cookie-manager";

const AuthrosLibrary: React.FC = () => {
  const history = useHistory();
  const user_local = getAuthCookie(true);
  const [user, setUser] = useState(user_local);
  const [activeReader, setActiveReader] = useState<AuthorReaderItem | false>(
    false,
  );
  const { lang_name } = useParams<{ lang_name: string }>();

  const { data: get_readers, loading } = useQuery(GET_AUTHOR_READERS, {
    variables: {
      filter: {
        lang_id: user.lang_id,
        published_only: true,
      },
    },
    fetchPolicy: "no-cache",
  });
  const dataContext = useContext(DataContext);
  const [readers, setReader] = useState<AuthorReaderItem[]>([]);
  const languages = JSON.parse(localStorage.getItem("local_languages") || "{}");

  useEffect(() => {
    const selected_lang: LangItem = languages.find(
      (lang: LangItem) => lang.id === user.lang_id,
    );
    if (selected_lang) {
      setUser({ ...user, lang_name: selected_lang.name });
    }

    return () => {
      const body = document.body;
      body.classList.remove("scroll_lock");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeReader) {
      const body = document.body;
      body.classList.remove("scroll_lock");
    } else {
      if (isMobile) {
        ///save active reader to local storage
        history.push(`/bookdetails/${activeReader.url}/${activeReader.id}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReader]);

  useEffect(() => {
    if (get_readers && get_readers.books) {
      const order_readers = get_readers.books.sort(
        (a: AuthorReaderItem, b: AuthorReaderItem) => {
          // Sort by coming_soon
          if (a.coming_soon && !b.coming_soon) {
            return -1;
          } else if (!a.coming_soon && b.coming_soon) {
            return 1;
          }
          // Sort by is_new (if exists)
          if (a.is_new && !b.is_new) {
            return -1;
          } else if (!a.is_new && b.is_new) {
            return 1;
          }
          // Sort by rank
          return a.rank > b.rank ? 1 : -1;
        },
      );
      setReader(order_readers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get_readers]);

  useEffect(() => {
    if (lang_name) {
      const selected_lang: LangItem = languages.find(
        (lang: LangItem) => lang.name.toLocaleLowerCase() === lang_name,
      );
      if (selected_lang) {
        dataContext.setLang(selected_lang.id);
        dataContext.setLoaded(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang_name]);

  const slectActiveReader = (ev: { currentTarget: HTMLElement }) => {
    const reader_id = ev.currentTarget.dataset.id;
    const reader = readers.find((reader) => reader.id === reader_id);
    if (reader) {
      setActiveReader(reader);
      const body = document.body;
      body.classList.add("scroll_lock");
    }
  };

  const handleBack = () => {
    setActiveReader(false);
  };

  return (
    <main>
      <Helmet title={`${user.lang_name || ""} Library | Flangoo`} />
      {activeReader ? (
        <div className="book_details_library">
          {" "}
          <BookInfo
            row_title="Creator Books"
            reader={activeReader}
            goBack={handleBack}
            user={user}
          />
        </div>
      ) : null}
      <div
        className={`first-container last-container container-fluid text-center books_page ${
          activeReader ? "active_reader" : ""
        }`}
      >
        <div className="title_part">
          <div className="container text-center">
            <h1 className="m-0">New Creators' Books!</h1>
            <p
              style={{
                maxWidth: "80%",
                margin: "auto",
              }}
            >
              Discover a world of knowledge crafted by educators for educators.
              Explore teacher-created books designed to inspire and enhance your
              teaching journey. <br />
              For teachers by teachers!
            </p>
          </div>
        </div>
        <CreatorInvite />
        <LanguageSelect />
        {loading ? <SkeletonBooks count={10} /> : null}
        <div className="authors_books_container">
          {readers
            ? readers.map((reader) => {
                return (
                  <ReaderBlock
                    disabled={!hasPrivilege(user.privileges, "creator")}
                    key={`reader_${reader.id}`}
                    reader={reader}
                    activeReader={activeReader ? activeReader.id : false}
                    slectActiveReader={slectActiveReader}
                  />
                );
              })
            : null}
        </div>
        <hr />
        <CreatorInvite />
      </div>
    </main>
  );
};

const CreatorInvite = () => {
  const user = getAuthCookie(true);
  return (
    <div className="title_part">
      <div className="container text-center">
        <p>Want to publish your story?</p>
        {hasPrivilege(user.privileges, "creator") ? (
          <a
            target={"_blank"}
            rel="noreferrer"
            className="btn btn-primary"
            href={
              process.env.REACT_AUTHORS_URL || "https://creator.flangoo.com"
            }
          >
            <span>
              Write Your Story<i className="fas fa-pen-alt text-white"></i>
            </span>
          </a>
        ) : (
          <Link to={"/purchaseplan"} className="btn btn-primary">
            <span>
              Become a Creator <i className="fas fa-pen-alt text-white"></i>
            </span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default AuthrosLibrary;
